import React, { useEffect, useState } from 'react'
import { useUser } from 'util/UserContext'
import { useNavigate, useParams } from 'react-router-dom'
import TeamSettingsProfile from './TeamSettingsProfile'
import TeamSettingsPrivacy from './TeamSettingsPrivacy'
import TeamSettingsMembers from './TeamSettingsMembers'
import ScrollableTabsList from 'ui/ScrollableTabsList'
import api from 'config/axiosConfig'
import { Team } from 'util/constants'
import NotFoundPage from 'components/ResponsePages/404NotFoundPage'
import ServerErrorPage from 'components/ResponsePages/500ServerErrorPage'
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from 'ui/InputSelect'
import { Users } from 'lucide-react'

const TeamSettings: React.FC = () => {
    const { currentUser, awaitingUser } = useUser()
    const navigate = useNavigate()
    const { teamname, tab } = useParams<{ teamname: string; tab: string }>()
    const [selectedTab, setSelectedTab] = useState<string>(tab || 'profile')
    const [team, setTeam] = useState<Team | null>(null)
    const [moderatedTeams, setModeratedTeams] = useState<Team[]>([])
    const [error, setError] = useState<string>('')
    const [loading, setLoading] = useState<boolean>(true)

    // Redirect if not logged in
    useEffect(() => {
        if (!awaitingUser && !currentUser) {
            navigate('/', { replace: true })
        }
    }, [currentUser, awaitingUser, navigate])

    // Fetch moderated teams
    useEffect(() => {
        const fetchModeratedTeams = async () => {
            try {
                const response = await api.get<Team[]>('/api/team/moderated')
                setModeratedTeams(response.data)
            } catch (error) {
                console.error('Error fetching moderated teams:', error)
                setError('Failed to fetch moderated teams')
            }
        }

        if (currentUser) {
            fetchModeratedTeams()
        }
    }, [currentUser])

    // Fetch team data and verify permissions
    useEffect(() => {
        const fetchTeam = async () => {
            try {
                const response = await api.get<Team>(`/api/team/${teamname}`)
                const teamData = response.data
                
                // Verify user has permission (creator or moderator)
                if (!awaitingUser && teamData.properties_public?.creator_id !== currentUser?.id) {
                    // Check if user is a moderator of this team
                    const isModeratedTeam = moderatedTeams.some(team => team.teamname === teamname)
                    if (!isModeratedTeam) {
                        navigate(`/partea/${teamname}`)
                        return
                    }
                }
                
                setTeam(teamData)
                setLoading(false)
            } catch (error) {
                setLoading(false)
                setError('Failed to fetch team data')
            }
        }

        if (teamname) {
            fetchTeam()
        }
    }, [teamname, currentUser, navigate, moderatedTeams, awaitingUser])

    // Update selectedTab when URL tab parameter changes
    useEffect(() => {
        if (tab) {
            setSelectedTab(tab)
        } else {
            setSelectedTab('profile')
        }
    }, [tab])

    const handleTeamChange = (value: string) => {
        navigate(`/partea/${value}/settings/${selectedTab}`)
    }

    if (loading) {
        return <div>Loading...</div>
    }

    if (error) {
        return <ServerErrorPage />
    }

    if (!team) {
        return <NotFoundPage displayStyle="page" errorMessage="Sorry, that Partea doesn't exist." />
    }

    const tabsData = [
        {
            label: 'Profile',
            route: `/partea/${teamname}/settings/profile`,
            value: 'profile',
        },
        {
            label: 'Privacy',
            route: `/partea/${teamname}/settings/privacy`,
            value: 'privacy',
        },
        {
            label: 'Members',
            route: `/partea/${teamname}/settings/members`,
            value: 'members',
        },
    ]

    return (
        <div className="max-w-4xl m-4">
            <h1 className="flex flex-row flex-wrap gap-x-1 my-2 items-center text-eoe-text-heading dark:text-eoe-dark-text-heading text-xl font-bold">
                {`Settings ❯`}&nbsp;
                
                <Select onValueChange={handleTeamChange} value={teamname}>
                    <SelectTrigger className="px-2 py-0 rounded-full text-base flex-shrink bg-transparent border border-eoe-text dark:border-eoe-dark-text hover:bg-eoe-icon-hover dark:hover:bg-eoe-dark-icon-hover">
                        <SelectValue placeholder="Select a team" />
                    </SelectTrigger>
                    <SelectContent className="border border-eoe-border-main dark:border-eoe-dark-border-main text-base">
                        {moderatedTeams.map((moderatedTeam) => (
                            <SelectItem key={moderatedTeam.id} value={moderatedTeam.teamname}>
                                <div className="flex items-center gap-2">
                                <div className="flex items-center gap-2">
                                    <Users className='size-4' />
                                    <span>{moderatedTeam.teamname}</span>
                                </div>
                                </div>
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </h1>
            
            {/* Tabs Navigation */}
            <div className="horizontal-tablist my-2">
                <ScrollableTabsList
                    key="team-settings-tabs"
                    tabs={tabsData}
                    selectedTab={selectedTab}
                    onTabChange={() => {}}
                    pageTitle={`Settings > ${teamname}`}
                />
            </div>

            {/* Tabs Content */}
            <div className="">
                {selectedTab === 'profile' && (
                    <div
                        role="tabpanel"
                        id="profile-panel"
                        aria-labelledby="profile-tab"
                        className="focus:outline-none"
                    >
                        <TeamSettingsProfile team={team} />
                    </div>
                )}
                {selectedTab === 'privacy' && (
                    <div
                        role="tabpanel"
                        id="privacy-panel"
                        aria-labelledby="privacy-tab"
                        className="focus:outline-none"
                    >
                        <TeamSettingsPrivacy team={team} />
                    </div>
                )}
                {selectedTab === 'members' && (
                    <div
                        role="tabpanel"
                        id="members-panel"
                        aria-labelledby="members-tab"
                        className="focus:outline-none"
                    >
                        <TeamSettingsMembers team={team} />
                    </div>
                )}
            </div>
        </div>
    )
}

export default TeamSettings 